import { h, Fragment } from 'preact';
import { useRef, useEffect, useState } from 'preact/hooks';
import useFormStore from '../../store/useFormStore';
import { checkPromotionConditions } from '../../util/helper';

// Cookie helper functions
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const setCookie = (name, value, hours = 24) => {
  const date = new Date();
  date.setTime(date.getTime() + (hours * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax;Secure`;
};

/**
 * Flash Sale Promotion Block
 * Displays active flash sale promotions with image and text
 * If flash_sale_popup is true, renders as a modal using the dialog element
 */
export default function PromotionFlashSaleBlock() {
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const manuallyEnteredPromoCodes = useFormStore((state) => state.manuallyEnteredPromoCodes);
  const addManualPromoCode = useFormStore((state) => state.addManualPromoCode);
  const appliedPromotions = useFormStore((state) => state.appliedPromotions);
  const cart = useFormStore((state) => state.cart);

  const dialogRef = useRef(null);
  const [applyingPromo, setApplyingPromo] = useState(null);

  // Get flash sale promotions
  const getFlashSalePromotions = () => {
    if (!currentFranchise?.promotions || currentFranchise.promotions.length === 0) {
      return [];
    }

    return currentFranchise.promotions.filter(promo => {
      if (!promo.flash_sale) return false;

      // Check if date is valid
      if (promo.date) {
        const now = new Date();
        const startDate = promo.date.start ? new Date(promo.date.start) : null;
        const endDate = promo.date.end ? new Date(promo.date.end) : null;

        if (startDate && now < startDate) return false;
        if (endDate && now > endDate) return false;
      }

      // Check if conditions are met using helper
      if (!checkPromotionConditions(promo, cart)) {
        return false;
      }

      return true;
    });
  };

  const flashSalePromotions = getFlashSalePromotions();
  const popupPromotions = flashSalePromotions.filter(promo => promo.flash_sale_popup);
  const sidebarPromotions = flashSalePromotions.filter(promo => !promo.flash_sale_popup);

  // Open dialog when popup promotions exist with delay and cookie check
  useEffect(() => {
    if (popupPromotions.length > 0 && dialogRef.current) {
      // Check if user has already seen the popup in the last 24 hours
      const hasSeenPopup = getCookie('flash_sale_popup_seen');

      if (!hasSeenPopup) {
        // Add 10s delay before showing popup
        const timer = setTimeout(() => {
          if (dialogRef.current) {
            dialogRef.current.showModal();
          }
        }, 10000);

        return () => clearTimeout(timer);
      }
    }
  }, [popupPromotions.length]);

  const handleClose = () => {
    if (dialogRef.current) {
      dialogRef.current.close();
      // Set cookie to prevent showing popup again for 24 hours
      setCookie('flash_sale_popup_seen', 'true', 24);
    }
  };

  const handleApplyPromo = async (promo) => {
    setApplyingPromo(promo.id);

    try {
      // Check if already manually entered
      if (manuallyEnteredPromoCodes.includes(promo.promo_code)) {
        setApplyingPromo(null);
        return;
      }

      // Add to manually entered codes
      addManualPromoCode(promo.promo_code);

      // Wait for cart validation to complete
      await new Promise(resolve => setTimeout(resolve, 100));

      // Check if it was actually applied
      const state = useFormStore.getState();
      let shouldClosePopup = false;

      if (promo.requirement === false) {
        shouldClosePopup = true;
      } else if (state.appliedPromotions.includes(promo.promo_code)) {
        shouldClosePopup = true;
      } else {
        // Promo not valid, remove it
        state.removeManualPromoCode(promo.promo_code);
      }

      // Close popup if promo was successfully applied
      if (shouldClosePopup && dialogRef.current && dialogRef.current.open) {
        // Small delay before closing
        await new Promise(resolve => setTimeout(resolve, 300));
        handleClose();
      }
    } catch (error) {
      console.error('Error applying promo code:', error);
    } finally {
      setApplyingPromo(null);
    }
  };

  const renderPromotionContent = (promo) => {
    const displayText = promo.text || promo.description || promo.title;
    const isApplied = appliedPromotions.includes(promo.promo_code) || manuallyEnteredPromoCodes.includes(promo.promo_code);
    const isApplying = applyingPromo === promo.id;

    return (
      <div key={promo.id} className="flex flex-col gap-4">
        {/* Image Section */}
        {promo.image && (
          <div className="relative w-full">
            <img
              src={promo.image}
              alt={promo.title}
              className="w-full h-auto max-w-full"
              loading="lazy"
            />
          </div>
        )}
        {/* Content Section */}
        <div
          className="prose prose-sm max-w-none [&>*:last-child]:mb-0"
          dangerouslySetInnerHTML={{ __html: displayText }}
        />

        {/* Apply Button */}
        {promo.promo_code && !isApplied && (
          <button
            onClick={() => handleApplyPromo(promo)}
            disabled={isApplying}
            className={`coit-button ${isApplying ? 'opacity-50 cursor-wait' : ''}`}
          >
            {isApplying ? 'APPLYING...' : 'APPLY'}
          </button>
        )}
      </div>
    );
  };

  // No flash sales to show
  if (flashSalePromotions.length === 0) {
    return null;
  }

  return (
    <>
      {/* Dialog modal for popup promotions */}
      {popupPromotions.length > 0 && (
        <dialog
          ref={dialogRef}
          className="backdrop:bg-black backdrop:opacity-50 rounded-lg shadow-xl max-w-[90vw] md:max-w-2xl w-full p-0 border-0"
        >
          <div className="relative bg-white rounded-lg max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-lg hover:bg-coit-red transition-colors text-black hover:text-white cursor-pointer"
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Modal Content */}
            <div className="p-6">
              {popupPromotions.map(renderPromotionContent)}
            </div>
          </div>
        </dialog>
      )}

      {/* Sidebar promotions */}
      {sidebarPromotions.length > 0 && (
        <div className="promotion-flash-sale-block bg-white p-6 shadow-sm">
          {sidebarPromotions.map(renderPromotionContent)}
        </div>
      )}
    </>
  );
}
